import React, { useEffect, useState, useMemo } from 'react';
import {
  Card, ProductShort, ProductDiv, Button, ActionsDiv, FormGrid, FormGroup, Label,
  Select, HeaderLine, Td, Th, Tr, InputLabel, Summary, SummaryItem, SummaryLabel,
  SummaryValue, Separator, ButtonsDiv, AddButton, AddButtonImg, AddButtonText,
  CancelButton, CancelButtonText, RemoveButton, RemoveButtonImg,
  RemoveButtontext
} from './style';

import rectangle from '../../../../assets/fashion/TableFashionHeader/rectangle.svg';
import trash from '../../../../assets/fashion/TableFashionHeader/trash.svg';
import whitePlus from '../../../../assets/fashion/TableFashionHeader/whitePlus.svg';
import remove from '../../../../assets/fashion/Actions/remove.svg';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Outlet } from 'react-router-dom';
import axios from 'axios';

// --- Interfaces de DTOs para o Frontend (Espelham o que o Backend espera) ---

// Define a estrutura de cada item de cor/tamanho que será enviado para o backend
interface FashionOrderProductItemInput {
  colorId: number;
  size: string;
  quantity: number;
}

// Define a estrutura completa de um objeto de cor, vindo da API de famílias
interface ColorData {
  id: number;
  name: string;
}

// Adapta a interface ProductApiData para a nova estrutura que será enviada/recebida
interface ProductApiData {
  id: number;
  fashionProductId: number;
  discount: number | null;
  totalItems: number; // Somatório das quantidades de todos os items (cores/tamanhos)
  subtotal: number; // Subtotal calculado com base em totalItems e discount
  items: FashionOrderProductItemInput[]; // Array de itens de cor/tamanho

  // Propriedades para exibição/preenchimento no frontend ou retorno da API
  fashionOrderId?: number;
  fashionProduct?: { // 'fashionProduct' pode ser opcional ou sempre presente dependendo do contexto
    id: number;
    name: string;
    price: number;
    size: string[]; // Ex: ["34", "36", "38"]
    group: { id: number; name?: string; family?: { id: number; name?:string; collectionId?: number; } };
  };
  colorCount?: number; // Pode ser calculado no backend se não for mais um campo persistente
  colors?: string[]; // Array de Nomes das cores para exibição no frontend (ex: ["Azul", "Verde"])
  family?: { id: number; Corileone?: { name: string }; name?: string; familyId?: number; };
  group?: { id: number; name?: string };
  name?: string; // Para compatibilidade
  price?: number; // Para compatibilidade
  totalQuantity?: number; // Para compatibilidade (mesmo que totalItems)
  totalSubtotal?: number; // Para compatibilidade (mesmo que subtotal)
}

// Interfaces de dados da API (como FamilyData e GroupData)
interface FamilyData {
  id: number;
  name: string;
  groups?: GroupData[];
  colors?: ColorData[]; // Agora armazena objetos ColorData
}

interface GroupData {
  id: number;
  name: string;
  products?: ProductApiData[]; // Note que ProductApiData aqui ainda pode ter a estrutura antiga se a API de busca de produtos não foi atualizada
}


interface PropsType {
  order: any[];
  setOrder: React.Dispatch<React.SetStateAction<any>>;
  product?: any;
  onClose?: () => void;
  onRemoveProduct: (index: number) => void;
  onProductUpdated: (oldProduct: any, updatedProduct: any) => void;
  onProductAdded?: any;
  isEditing: boolean;
}

const PedidoForm: React.FC<PropsType> = ({ order, setOrder, product, onClose, onRemoveProduct, onProductUpdated, onProductAdded, isEditing }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [selectedPrice, setSelectedPrice] = useState<number | null>(null);
  // availableColors agora armazena objetos ColorData { id: number; name: string }
  const [availableColors, setAvailableColors] = useState<ColorData[]>([]);
  // selectedColors agora mapeia colorId -> boolean para as cores selecionadas na UI
  const [selectedColors, setSelectedColors] = useState<{ [colorId: number]: boolean }>({});
  // Quantities agora é um Map: colorId -> { size -> quantity }
  const [quantities, setQuantities] = useState<Map<number, { [size: string]: number }>>(new Map());
  const [discount, setDiscount] = useState<string>('');

  const [localAddedProducts, setLocalAddedProducts] = useState<ProductApiData[]>([]);

  const [familiesData, setFamiliesData] = useState<FamilyData[]>([]);
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>('');
  const [groupsData, setGroupsData] = useState<GroupData[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [productsData, setProductsData] = useState<ProductApiData[]>([]);

  // Calcula totalQuantity e totalSubtotal dinamicamente a partir do Map 'quantities'
  const calculatedTotalQuantity = useMemo(() => {
    let sum = 0;
    quantities.forEach(sizeMap => {
      Object.values(sizeMap).forEach(qty => sum += qty);
    });
    return sum;
  }, [quantities]);

  const totalSelectedColors = useMemo(() => Object.values(selectedColors).filter(Boolean).length, [selectedColors]);

  const calculatedTotalSubtotal = useMemo(() => {
    if (selectedPrice === null) return 0;
    const rawTotal = calculatedTotalQuantity * selectedPrice;
    const discountValue = parseFloat(discount);
    const discountAmount = !isNaN(discountValue) ? (discountValue / 100) * rawTotal : 0;
    return rawTotal - discountAmount;
  }, [calculatedTotalQuantity, selectedPrice, discount]);

  const size = ["34", "36", "38", "40", "42", "44", "46", "48"];

  // 1. useEffect para buscar FAMÍLIAS (executa apenas uma vez ao montar o componente)
  useEffect(() => {
    const fetchFamilies = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_FASHION_ORDERS_BASE_URL}/families`);
        if (Array.isArray(response.data)) {
          setFamiliesData(response.data);
        } else {
          console.error('Dados de famílias não são um array:', response.data);
          setFamiliesData([]);
        }
      } catch (error) {
        console.error('Erro ao carregar famílias:', error);
        setFamiliesData([]);
      }
    };
    fetchFamilies();
  }, []);

  // 2. useEffect para filtrar GRUPOS quando a FAMÍLIA selecionada muda
  useEffect(() => {
    if (selectedFamilyId) {
      const family = familiesData.find(f => String(f.id) === selectedFamilyId);
      if (family) {
        setGroupsData(family.groups || []);
        // Armazena objetos ColorData { id: number; name: string }
        setAvailableColors(family.colors || []);

        if (isEditing && product && product.fashionProduct?.group?.family?.id && String(product.fashionProduct.group.family.id) === selectedFamilyId) {
            const initialGroupId = product.fashionProduct?.group?.id ? String(product.fashionProduct.group.id) : '';
            setSelectedGroupId(initialGroupId);
        } else {
            setSelectedGroupId('');
            setSelectedProductId(null);
            setSelectedPrice(null);
            setSelectedColors({}); // Limpa a seleção de cores
            setQuantities(new Map()); // Limpa as quantidades
        }
      } else {
        setGroupsData([]);
        setAvailableColors([]);
        setProductsData([]);
        setSelectedGroupId('');
        setSelectedProductId(null);
        setSelectedPrice(null);
        setSelectedColors({});
        setQuantities(new Map());
      }
    } else {
      setGroupsData([]);
      setAvailableColors([]);
      setProductsData([]);
      setSelectedGroupId('');
      setSelectedProductId(null);
      setSelectedPrice(null);
      setSelectedColors({});
      setQuantities(new Map());
    }
  }, [selectedFamilyId, familiesData, isEditing, product]);

  // 3. useEffect para filtrar PRODUTOS quando o GRUPO selecionado muda
  useEffect(() => {
    if (selectedGroupId) {
      const group = groupsData.find(g => String(g.id) === selectedGroupId);
      if (group && Array.isArray(group.products)) {
        setProductsData(group.products);

        if (isEditing && product && product.fashionProduct?.group?.id && String(product.fashionProduct.group.id) === selectedGroupId) {
            setSelectedProductId(product.fashionProductId || product.fashionProduct?.id || null);
            setSelectedPrice(product.fashionProduct?.price || null);
            setDiscount(String(product.discount || ''));

            // Preenche Cores e Quantidades no modo de edição
            const initialSelectedColors: { [colorId: number]: boolean } = {};
            const initialQuantitiesMap = new Map<number, { [size: string]: number }>();

            if (product.items && Array.isArray(product.items)) {
                product.items.forEach((item:any) => {
                    const colorId = item.colorId;
                    const size = item.size;
                    const quantity = item.quantity;

                    initialSelectedColors[colorId] = true; // Marca a cor como selecionada
                    if (!initialQuantitiesMap.has(colorId)) {
                        initialQuantitiesMap.set(colorId, {});
                    }
                    initialQuantitiesMap.get(colorId)![size] = quantity;
                });
            }
            setSelectedColors(initialSelectedColors);
            setQuantities(initialQuantitiesMap);
        } else {
            setSelectedProductId(null);
            setSelectedPrice(null);
            setSelectedColors({});
            setQuantities(new Map());
        }
      } else {
        setProductsData([]);
        setSelectedProductId(null);
        setSelectedPrice(null);
        setSelectedColors({});
        setQuantities(new Map());
      }
    } else {
      setProductsData([]);
      setSelectedProductId(null);
      setSelectedPrice(null);
      setSelectedColors({});
      setQuantities(new Map());
    }
  }, [selectedGroupId, groupsData, isEditing, product]);

  useEffect(() => {
    if (isEditing && product && familiesData.length > 0) {
      console.log("Iniciando preenchimento do formulário de edição com product:", product);
      const initialFamilyId = product.fashionProduct?.group?.family?.id ? String(product.fashionProduct.group.family.id) : '';
      setSelectedFamilyId(initialFamilyId);
    } else if (!isEditing) {
      setSelectedProductId(null);
      setSelectedFamilyId('');
      setSelectedGroupId('');
      setSelectedPrice(null);
      setAvailableColors([]);
      setSelectedColors({});
      setQuantities(new Map());
      setDiscount('');
    }
  }, [isEditing, product, familiesData]);

  useEffect(() => {
    if (Array.isArray(order)) {
      setLocalAddedProducts(order);
    } else {
      setLocalAddedProducts([]);
      console.warn('PedidoForm: Prop "order" recebida não é um array. Inicializando localAddedProducts como vazio.');
    }
  }, [order]);

  const handleProductSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = Number(event.target.value);
    setSelectedProductId(selectedId);

    const selectedProduct = productsData.find(p => p.id === selectedId);
    if (selectedProduct) {
      setSelectedPrice(selectedProduct.price || null);
    } else {
      setSelectedPrice(null);
    }
    setQuantities(new Map()); // Limpa quantidades ao mudar o produto
    setSelectedColors({}); // Limpa seleção de cores
  };

  const handleAddProduct = () => {
    const selectedProduct = productsData.find((p) => p.id === selectedProductId);
    const selectedFamilyObj = familiesData.find((f) => String(f.id) === selectedFamilyId);
    const selectedGroupObj = groupsData.find((g) => String(g.id) === selectedGroupId);

    if (!selectedProduct || !selectedFamilyObj || !selectedGroupObj || selectedPrice === null || totalSelectedColors === 0) {
      alert('Preencha todas as informações, incluindo pelo menos uma cor e quantidades.');
      return;
    }

    // Construir o array 'items' para enviar ao backend
    const itemsToSubmit: FashionOrderProductItemInput[] = [];
    let calculatedTotalItems = 0; // Para recalcular totalItems com base nos novos 'items'

    quantities.forEach((sizeMap, colorId) => {
        // Encontra o objeto de cor para obter o nome para exibição
        const colorObj = availableColors.find(c => c.id === colorId);
        if (!colorObj) return; // Se a cor não for encontrada, pule.

        Object.entries(sizeMap).forEach(([sizeItem, quantity]) => {
            if (quantity > 0) {
                itemsToSubmit.push({
                    colorId: colorId,
                    size: sizeItem,
                    quantity: quantity
                });
                calculatedTotalItems += quantity;
            }
        });
    });

    const newProductToAdd: ProductApiData = {
      id: 0,
      fashionProductId: selectedProduct.id,
      discount: parseFloat(discount),
      totalItems: calculatedTotalItems, // Usar o total calculado dos 'items'
      subtotal: calculatedTotalSubtotal, // Usar o subtotal calculado
      items: itemsToSubmit, // O novo array de itens de produto
      
      // Propriedades para exibição no frontend (nomes das cores)
      colors: availableColors
          .filter(color => selectedColors[color.id])
          .map(color => color.name),

      fashionProduct: {
        id: selectedProduct.id,
        name: selectedProduct.name || '',
        price: selectedPrice,
        size: selectedProduct.fashionProduct?.size || [],
        group: {
          id: selectedGroupObj.id,
          name: selectedGroupObj.name,
          family: {
            id: selectedFamilyObj.id,
            name: selectedFamilyObj.name,
            collectionId: selectedProduct.fashionProduct?.group?.family?.collectionId
          }
        }
      },
      family: {
        id: selectedFamilyObj.id,
        name: selectedFamilyObj.name,
        Corileone: { name: selectedFamilyObj.name },
        familyId: selectedFamilyObj.id
      },
      group: {
        id: selectedGroupObj.id,
        name: selectedGroupObj.name
      },
      fashionOrderId: product?.fashionOrderId
    };

    console.log('Novo produto a ser adicionado:', newProductToAdd);
    if (onProductAdded) {
      onProductAdded(newProductToAdd);
    } else {
      console.warn("PedidoForm: A prop 'onProductAdded' não foi fornecida. Adicionando produto ao estado local e via 'setOrder' (se disponível).");
      setLocalAddedProducts((prev) => [...prev, newProductToAdd]);
      setOrder((prev: any[]) => [...prev, newProductToAdd]);
    }

    setSelectedProductId(null);
    setSelectedFamilyId('');
    setSelectedGroupId('');
    setSelectedPrice(null);
    setAvailableColors([]);
    setSelectedColors({});
    setQuantities(new Map());
    setDiscount('');
    onClose?.();
  };

  const handleUpdateProduct = () => {
    console.log("handleUpdateProduct clicado!");

    const selectedProductData = productsData.find(p => p.id === selectedProductId);
    const selectedFamilyObj = familiesData.find(f => String(f.id) === selectedFamilyId);
    const selectedGroupObj = groupsData.find(g => String(g.id) === selectedGroupId);

    if (!selectedProductData || !selectedFamilyObj || !selectedGroupObj || selectedPrice === null || totalSelectedColors === 0) {
      console.log("Validação falhou: Dados incompletos.");
      alert('Por favor, selecione um produto, família, grupo, pelo menos uma cor e quantidades válidas.');
      return;
    }

    // Construir o array 'items' para enviar ao backend para atualização
    const itemsToSubmit: FashionOrderProductItemInput[] = [];
    let calculatedTotalItems = 0; // Para recalcular totalItems com base nos novos 'items'

    quantities.forEach((sizeMap, colorId) => {
        Object.entries(sizeMap).forEach(([sizeItem, quantity]) => {
            if (quantity > 0) {
                itemsToSubmit.push({
                    colorId: colorId,
                    size: sizeItem,
                    quantity: quantity
                });
                calculatedTotalItems += quantity;
            }
        });
    });

    const updatedProduct: ProductApiData = {
      ...(product as ProductApiData), // Mantém propriedades do produto original
      
      fashionProductId: selectedProductData.id,
      discount: parseFloat(discount),
      totalItems: calculatedTotalItems, // Usar o total calculado dos 'items'
      subtotal: calculatedTotalSubtotal, // Usar o subtotal calculado
      items: itemsToSubmit, // O novo array de itens de produto para atualização

      // Propriedades para exibição no frontend (nomes das cores)
      colors: availableColors
          .filter(color => selectedColors[color.id])
          .map(color => color.name),

      fashionProduct: {
        ...(product?.fashionProduct),
        id: selectedProductData?.id || 0,
        name: selectedProductData?.name || '',
        price: selectedPrice,
        group: {
          id: selectedGroupObj.id,
          name: selectedGroupObj.name,
          family: {
            id: selectedFamilyObj.id,
            name: selectedFamilyObj.name,
            collectionId: product?.fashionProduct?.group?.family?.collectionId
          }
        }
      },
      family: {
        id: selectedFamilyObj.id,
        name: selectedFamilyObj.name,
        Corileone: { name: selectedFamilyObj.name }, 
        familyId: selectedFamilyObj.id
      },
      group: {
        id: selectedGroupObj.id,
        name: selectedGroupObj.name
      }
    };

    console.log('Objeto updatedProduct construído:', updatedProduct);
    
    onProductUpdated(product, updatedProduct);
    
    onClose?.(); 
  };

  const handleRemoveProductLocal = (indexToRemove: number) => {
    onRemoveProduct(indexToRemove);
    onClose?.();
  };

  return (
    <>
      {!isEditing && Array.isArray(localAddedProducts) && localAddedProducts.length > 0 && (
        <>
          {localAddedProducts.map((prodItem, index) => {
            let familyName = '-';
            if (prodItem.family?.name) {
              familyName = prodItem.family.name;
            } else if (prodItem.fashionProduct?.group?.family?.name) {
              familyName = prodItem.fashionProduct.group.family.name;
            } else if (prodItem.fashionProduct?.group?.family?.id) {
                const foundFamilyFromData = familiesData.find((f:any) => f.id === prodItem?.fashionProduct?.group?.family?.id);
                familyName = foundFamilyFromData?.name ?? `ID: ${prodItem.fashionProduct.group.family.id}`;
            }

            let groupName = '-';
            if (prodItem.group?.name) {
              groupName = prodItem.group.name;
            } else if (prodItem.fashionProduct?.group?.name) {
              groupName = prodItem.fashionProduct.group.name;
            } else if (prodItem.fashionProduct?.group?.id) {
                const foundGroupFromData = groupsData.find((g:any) => g.id === prodItem?.fashionProduct?.group?.id);
                groupName = foundGroupFromData?.name ?? `ID: ${prodItem.fashionProduct.group.id}`;
            }
            
            // AGORA SIM, USE prodItem.colorCount PARA EXIBIR O NÚMERO DE CORES
            const numberOfColorsToDisplay = prodItem.colorCount !== undefined && prodItem.colorCount !== null
                                             ? prodItem.colorCount
                                             : (prodItem.items && Array.isArray(prodItem.items)
                                                 ? new Set(prodItem.items.map(item => item.colorId)).size
                                                 : 0);

            const productFields = [
              { label: 'Produto', value: prodItem.fashionProduct?.name ?? prodItem.name ?? '-', showIcon: true },
              { label: 'Episódio', value: familyName, showIcon: true },
              { label: 'Cores', value: numberOfColorsToDisplay, showIcon: true }, // Exibir os nomes das cores
              { label: 'Precificação', value: `R$ ${(prodItem.fashionProduct?.price ?? prodItem.price ?? 0).toFixed(2).replace('.', ',')}`, showIcon: true },
              { label: 'Quantidade de peças', value: prodItem.totalItems ?? prodItem.totalQuantity ?? '-', showIcon: true },
              { label: 'Subtotal', value: `R$ ${(prodItem.subtotal ?? prodItem.totalSubtotal ?? 0).toFixed(2).replace('.', ',')}`, showIcon: false },
            ];

            return (
              <Card key={prodItem.fashionProductId || index}>
                <ProductShort>
                  <ProductDiv>
                    {productFields.map(({ label, value, showIcon }, i) => (
                      <Label key={i}>
                        <span style={{ fontWeight: 'normal' }}>{label}:</span> {value} {showIcon && <img style={{ marginLeft: '0.5rem'}} src={rectangle} alt="" />}
                      </Label>
                    ))}
                    <ActionsDiv className="flex items-center gap-2">
                        <img
                            src={trash}
                            alt="Remover"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleRemoveProductLocal(index)}
                        />
                        <Button onClick={() => setIsOpen(!isOpen)}>
                            {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                        </Button>
                    </ActionsDiv>
                  </ProductDiv>
                </ProductShort>
              </Card>
            );
          })}
        </>
      )}

      {isOpen && (
        <Card>
          <FormGrid className="grid grid-cols-3 gap-4 mt-4">
            <FormGroup>
              <Label>Episódio</Label>
              <Select
                value={selectedFamilyId ?? ''}
                onChange={(e) => {
                  setSelectedFamilyId(e.target.value);
                }}
              >
                <option value="">Selecione o Episódio</option>
                {Array.isArray(familiesData) && familiesData.map((family) => (
                  <option key={family.id} value={family.id}>
                    {family.name}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Grupo</Label>
              <Select
                value={selectedGroupId ?? ''}
                onChange={(e) => {
                  setSelectedGroupId(e.target.value);
                }}
                disabled={!selectedFamilyId || !Array.isArray(groupsData) || groupsData.length === 0}
              >
                <option value="">Selecione o Grupo</option>
                {Array.isArray(groupsData) && groupsData.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Produto</Label>
              <Select
                value={selectedProductId ?? ''}
                onChange={handleProductSelectChange}
                disabled={!selectedGroupId || !Array.isArray(productsData) || productsData.length === 0}
              >
                <option value="">Selecione o Produto</option>
                {Array.isArray(productsData) && productsData.map((prod:any) => (
                  <option key={prod.id} value={prod.id}>
                    {prod.name}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Desconto %</Label>
              <InputLabel
                type="number"
                step="0.1"
                min="0"
                max="100"
                className="w-full rounded bg-gray-100 p-2"
                value={discount}
                onChange={(e) => setDiscount(String(e.target.value))}
              />
            </FormGroup>

            <FormGroup>
              <Label className="block text-gray-500">Preçificação</Label>
              <div style={{'padding':'10px', 'fontWeight': '700', 'color': ' #303030' }}>
                R$ {selectedPrice ? selectedPrice.toFixed(2).replace('.', ',') : '0.00'}
              </div>
            </FormGroup>
          </FormGrid>

          {/* Tabela de Cores e Quantidades */}
          {selectedFamilyId !== '' && ( // A tabela de cores só aparece se uma família for selecionada
            <>
              <thead>
                <HeaderLine>
                  <Th></Th>
                  {size.map((item, index) => (
                    <Th key={index}>{item}</Th>
                  ))}
                </HeaderLine>
              </thead>
              <tbody>
                {availableColors.map((colorItem) => (
                  <Tr key={colorItem.id}>
                    <Td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={!!selectedColors[colorItem.id]}
                          onChange={() => {
                            setSelectedColors((prev) => {
                              const newSelected = {
                                ...prev,
                                [colorItem.id]: !prev[colorItem.id],
                              };
                              if (prev[colorItem.id]) {
                                // Se desmarcou, remove todas as quantidades para aquela cor
                                setQuantities((prevQuantitiesMap) => {
                                  const updatedMap = new Map(prevQuantitiesMap);
                                  updatedMap.delete(colorItem.id); // Remove a entrada completa para essa cor
                                  return updatedMap;
                                });
                              }
                              return newSelected;
                            });
                          }}
                        />
                        {colorItem.name}
                      </div>
                    </Td>
                    {size.map((sizeItem, sizeIndex) => (
                      <Td key={sizeIndex}>
                        <InputLabel
                          type='number'
                          min='0'
                          disabled={!selectedColors[colorItem.id]}
                          // Acessa a quantidade do Map: quantities.get(colorId)?.[size]
                          value={quantities.get(colorItem.id)?.[sizeItem] || ''}
                          onChange={(e) => {
                            const value = parseInt(e.target.value);
                            setQuantities((prevQuantitiesMap) => {
                              const updatedMap = new Map(prevQuantitiesMap);
                              // Garante que o mapa interno para a cor exista
                              if (!updatedMap.has(colorItem.id)) {
                                updatedMap.set(colorItem.id, {});
                              }
                              // Atualiza a quantidade para o tamanho específico dentro da cor
                              updatedMap.get(colorItem.id)![sizeItem] = isNaN(value) ? 0 : value;
                              return updatedMap;
                            });
                          }}
                        />
                      </Td>
                    ))}
                  </Tr>
                ))}
              </tbody>
            </>
          )}

          <Separator />

          {/* Resumo de Cores, Quantidade de Peças, Subtotal */}
          <Summary>
            <SummaryItem>
              <SummaryLabel className="text-gray-500">Cores:</SummaryLabel>
              <SummaryValue>{totalSelectedColors}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel className="text-gray-500">Quantidade de peças:</SummaryLabel>
              <SummaryValue>{calculatedTotalQuantity}</SummaryValue> {/* Usar calculatedTotalQuantity */}
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel className="text-gray-500">Subtotal:</SummaryLabel>
              <SummaryValue>
                R$ {calculatedTotalSubtotal.toFixed(2).replace('.', ',')} {/* Usar calculatedTotalSubtotal */}
              </SummaryValue>
            </SummaryItem>
          </Summary>

          <Separator />

          {/* Botões de Ação */}
          <ButtonsDiv>
            {isEditing ? (
              <AddButton onClick={handleUpdateProduct}>
                <AddButtonImg src={whitePlus} />
                <AddButtonText>Salvar Alterações</AddButtonText>
              </AddButton>
            ) : (
              <AddButton onClick={handleAddProduct}>
                <AddButtonImg src={whitePlus} />
                <AddButtonText>Adicionar ao pedido</AddButtonText>
                <Outlet context={{ addedProducts: localAddedProducts, setAddedProducts: setLocalAddedProducts }} />
              </AddButton>
            )}
            <CancelButton onClick={() => onClose?.()}>
              <CancelButtonText>Cancelar</CancelButtonText>
            </CancelButton>
            {isEditing && (
              <RemoveButton onClick={() => onRemoveProduct(localAddedProducts.findIndex(p => p.fashionProductId === product?.fashionProductId))}>
                <RemoveButtonImg src={remove} />
                <RemoveButtontext>Remover</RemoveButtontext>
              </RemoveButton>
            )}
          </ButtonsDiv>
        </Card>
      )}
    </>
  );
};

export default PedidoForm;
